import { openDB, type DBSchema, type IDBPDatabase } from "idb"
import type { QueueItem } from "../types"
import type { DynamicUser, DynamicSchema } from "../types/dynamic"

interface PanelDB extends DBSchema {
  queue: {
    key: number
    value: QueueItem
  }
  // Stores del sistema anterior: ya no se usan, pero se conservan para no cambiar la versión de la BD local
  asistentes: {
    key: string
    value: unknown
  }
  prefixes: {
    key: number
    value: unknown
  }
  categories: {
    key: number
    value: unknown
  }
  certifications: {
    key: number
    value: unknown
  }
  couponPrefixes: {
    key: number
    value: unknown
  }
  groups: {
    key: string
    value: unknown
  }
  // New stores for dynamic system
  dynamicUsers: {
    key: number
    value: DynamicUser
  }
  dynamicSchema: {
    key: string
    value: DynamicSchema & { key: string }
  }
}

let db: IDBPDatabase<PanelDB> | null = null

export const initDB = async (): Promise<IDBPDatabase<PanelDB>> => {
  if (db) return db

  try {
    db = await openDB<PanelDB>("panel-usuarios-app", 7, {
      upgrade(db, oldVersion) {
        // Clear old stores if they exist
        if (oldVersion < 4) {
          if (db.objectStoreNames.contains("asistentes")) {
            db.deleteObjectStore("asistentes")
          }
          if (db.objectStoreNames.contains("queue")) {
            db.deleteObjectStore("queue")
          }
          if (db.objectStoreNames.contains("prefixes")) {
            db.deleteObjectStore("prefixes")
          }
          if (db.objectStoreNames.contains("categories")) {
            db.deleteObjectStore("categories")
          }
          if (db.objectStoreNames.contains("certifications")) {
            db.deleteObjectStore("certifications")
          }
        }

        // Create asistentes store
        if (!db.objectStoreNames.contains("asistentes")) {
          db.createObjectStore("asistentes", { keyPath: "asistente_folio" })
        }

        // Create queue store with auto-increment
        if (!db.objectStoreNames.contains("queue")) {
          db.createObjectStore("queue", { autoIncrement: true })
        }

        // Create prefixes store
        if (!db.objectStoreNames.contains("prefixes")) {
          db.createObjectStore("prefixes", { keyPath: "id" })
        }

        // Create categories store
        if (!db.objectStoreNames.contains("categories")) {
          db.createObjectStore("categories", { keyPath: "id" })
        }

        // Create certifications store
        if (!db.objectStoreNames.contains("certifications")) {
          db.createObjectStore("certifications", { keyPath: "id" })
        }

        // Create couponPrefixes store for version 5
        if (oldVersion < 5 && !db.objectStoreNames.contains("couponPrefixes")) {
          db.createObjectStore("couponPrefixes", { keyPath: "id" })
        }

        // Create groups store for version 6
        if (oldVersion < 6 && !db.objectStoreNames.contains("groups")) {
          db.createObjectStore("groups", { keyPath: "key" })
        }

        // Create dynamic system stores for version 7
        if (oldVersion < 7) {
          if (!db.objectStoreNames.contains("dynamicUsers")) {
            db.createObjectStore("dynamicUsers", { keyPath: "id" })
          }
          if (!db.objectStoreNames.contains("dynamicSchema")) {
            db.createObjectStore("dynamicSchema", { keyPath: "key" })
          }
        }
      },
    })

    console.log("💾 IndexedDB inicializada correctamente")
    return db
  } catch (error) {
    console.error("❌ Error inicializando IndexedDB:", error)
    throw error
  }
}

// Clear all data (for debugging)
export const clearAllData = async (): Promise<void> => {
  try {
    const database = await initDB()
    const tx = database.transaction(["asistentes", "queue", "prefixes", "categories", "certifications", "couponPrefixes", "groups", "dynamicUsers", "dynamicSchema"], "readwrite")
    await tx.objectStore("asistentes").clear()
    await tx.objectStore("queue").clear()
    await tx.objectStore("prefixes").clear()
    await tx.objectStore("categories").clear()
    await tx.objectStore("certifications").clear()
    await tx.objectStore("couponPrefixes").clear()
    await tx.objectStore("groups").clear()
    await tx.objectStore("dynamicUsers").clear()
    await tx.objectStore("dynamicSchema").clear()
    await tx.done
    console.log("🗑️ Todos los datos limpiados")
  } catch (error) {
    console.error("❌ Error limpiando datos:", error)
  }
}

// Queue operations
export const addToQueueDB = async (item: Omit<QueueItem, "id">): Promise<number> => {
  try {
    const database = await initDB()
    const key = await database.add("queue", item as QueueItem)
    console.log(`📝 Elemento agregado a la cola con key ${key}:`, item.type)
    return key as number
  } catch (error) {
    console.error("❌ Error agregando a la cola:", error)
    throw error
  }
}

export const getQueueFromDB = async (): Promise<Array<QueueItem & { id: number }>> => {
  try {
    const database = await initDB()
    const tx = database.transaction("queue", "readonly")
    const store = tx.objectStore("queue")

    const items: Array<QueueItem & { id: number }> = []
    let cursor = await store.openCursor()

    while (cursor) {
      items.push({
        id: cursor.key as number,
        ...cursor.value,
      })
      cursor = await cursor.continue()
    }

    return items
  } catch (error) {
    console.error("❌ Error obteniendo cola:", error)
    return []
  }
}

export const removeFromQueueDB = async (id: number): Promise<void> => {
  try {
    const database = await initDB()
    await database.delete("queue", id)
    console.log(`🗑️ Elemento ${id} eliminado de la cola`)
  } catch (error) {
    console.error("❌ Error eliminando de la cola:", error)
    throw error
  }
}

export const clearQueueDB = async (): Promise<void> => {
  try {
    const database = await initDB()
    await database.clear("queue")
    console.log("🗑️ Cola limpiada completamente")
  } catch (error) {
    console.error("❌ Error limpiando cola:", error)
    throw error
  }
}

// Dynamic Users operations
export const saveDynamicUsersToDb = async (users: DynamicUser[]): Promise<void> => {
  try {
    const database = await initDB()
    const tx = database.transaction("dynamicUsers", "readwrite")

    // Clear existing data
    await tx.store.clear()

    // Add new data
    for (const user of users) {
      if (user && user.id) {
        await tx.store.put(user)
      }
    }

    await tx.done
    console.log(`💾 ${users.length} usuarios dinámicos guardados en IndexedDB`)
  } catch (error) {
    console.error("❌ Error guardando usuarios dinámicos:", error)
    throw error
  }
}

export const getDynamicUsersFromDB = async (): Promise<DynamicUser[]> => {
  try {
    const database = await initDB()
    const users = await database.getAll("dynamicUsers")
    console.log(`📱 ${users.length} usuarios dinámicos cargados desde IndexedDB`)
    return users
  } catch (error) {
    console.error("❌ Error cargando usuarios dinámicos:", error)
    return []
  }
}

export const updateDynamicUserInDB = async (user: DynamicUser): Promise<void> => {
  try {
    if (!user || !user.id) {
      throw new Error("Datos de usuario dinámico inválidos")
    }

    const database = await initDB()
    await database.put("dynamicUsers", user)
    console.log(`💾 Usuario dinámico ${user.id} actualizado en IndexedDB`)
  } catch (error) {
    console.error("❌ Error actualizando usuario dinámico:", error)
    throw error
  }
}

export const addDynamicUserToDB = async (user: DynamicUser): Promise<void> => {
  console.log("⚠️ Usuario dinamico:", user)
  try {
    if (!user || !user.id) {
      throw new Error("Datos de usuario dinámico inválidos")
    }

    const database = await initDB()
    // Usar put en lugar de add para permitir actualizaciones
    await database.put("dynamicUsers", user)
    console.log(`💾 Usuario dinámico ${user.id} guardado en IndexedDB`)
  } catch (error) {
    console.error("❌ Error agregando usuario dinámico:", error)
    throw error
  }
}

export const deleteDynamicUserFromDB = async (id: number): Promise<void> => {
  const database = await initDB()
  await database.delete("dynamicUsers", id)
  console.log(`🗑️ Usuario dinámico ${id} eliminado de IndexedDB`)
}

// Nueva función para merge offline-first de usuarios dinámicos
// Gana el servidor, salvo los usuarios con cambios locales aún en cola (keepLocal), que conservan lo local.
export const mergeDynamicUsersToDb = async (
  serverUsers: DynamicUser[],
  keepLocal: (serverUser: DynamicUser) => boolean = () => false
): Promise<void> => {
  try {
    const database = await initDB()
    
    // Obtener usuarios locales actuales
    const localUsers = await getDynamicUsersFromDB()
    
    // Crear mapa de usuarios locales por ID
    const localUsersMap = new Map<number, DynamicUser>()
    localUsers.forEach(user => localUsersMap.set(user.id, user))
    
    console.log(`🔄 Mergeando ${serverUsers.length} usuarios del servidor con ${localUsers.length} usuarios locales`)
    
    // Hacer merge sin transacción compleja - agregar/actualizar usuarios del servidor
    // que no están en local o que tienen cambios
    for (const serverUser of serverUsers) {
      const localUser = localUsersMap.get(serverUser.id)
      
      if (!localUser) {
        // Usuario nuevo del servidor - agregarlo
        await database.put("dynamicUsers", serverUser)
        console.log(`📥 Usuario nuevo del servidor agregado: ID ${serverUser.id}`)
      } else {
        // Usuario existe en local - comparar y actualizar solo si hay diferencias significativas
        const serverJson = JSON.stringify(serverUser)
        const localJson = JSON.stringify(localUser)
        
        if (serverJson !== localJson) {
          if (keepLocal(serverUser)) {
            // Tiene cambios pendientes en la cola: conservar los datos locales hasta que se envíen
            const mergedUser = {
              ...serverUser, // Base del servidor
              ...localUser,  // Sobrescribir con datos locales (prioridad local)
              id: serverUser.id // Mantener ID consistente
            }

            await database.put("dynamicUsers", mergedUser)
            console.log(`🔄 Usuario con cambios en cola, se conserva lo local: ID ${serverUser.id}`)
          } else {
            // Sin cambios pendientes: la copia offline queda igual que el servidor (lo mismo que se ve en la tabla)
            await database.put("dynamicUsers", serverUser)
            console.log(`🔄 Usuario actualizado desde el servidor: ID ${serverUser.id}`)
          }
        }
      }
    }
    
    console.log(`✅ Merge completado - ${serverUsers.length} usuarios del servidor procesados`)
  } catch (error) {
    console.error("❌ Error mergeando usuarios dinámicos:", error)
    throw error
  }
}

// Dynamic Schema operations
export const saveDynamicSchemaToDb = async (schema: DynamicSchema): Promise<void> => {
  try {
    const database = await initDB()
    const tx = database.transaction("dynamicSchema", "readwrite")

    // Clear existing data
    await tx.store.clear()

    // Save schema as a single entry with proper typing
    const schemaWithKey = { key: "current_schema", ...schema } as DynamicSchema & { key: string }
    await tx.store.put(schemaWithKey)

    await tx.done
    console.log(`💾 Schema dinámico guardado en IndexedDB con ${Object.keys(schema).length} campos`)
  } catch (error) {
    console.error("❌ Error guardando schema dinámico:", error)
    throw error
  }
}

export const getDynamicSchemaFromDB = async (): Promise<DynamicSchema | null> => {
  try {
    const database = await initDB()
    const schemaData = await database.get("dynamicSchema", "current_schema")
    
    if (schemaData) {
      // Remove the 'key' property and return the rest
      const { key, ...schema } = schemaData
      console.log(`📱 Schema dinámico cargado desde IndexedDB con ${Object.keys(schema).length} campos`)
      return schema as DynamicSchema
    }

    console.log(`📱 No hay schema dinámico guardado en IndexedDB`)
    return null
  } catch (error) {
    console.error("❌ Error cargando schema dinámico:", error)
    return null
  }
}

// Function to clean temporary duplicate users
export const cleanTemporaryDuplicates = async (temporaryUsers: DynamicUser[]): Promise<void> => {
  try {
    const database = await initDB()
    
    console.log(`🧹 Eliminando ${temporaryUsers.length} registros temporales duplicados`)
    
    for (const tempUser of temporaryUsers) {
      await database.delete("dynamicUsers", tempUser.id)
      console.log(`🗑️ Registro temporal eliminado: ID ${tempUser.id}`)
    }
    
    console.log(`✅ Limpieza de registros temporales completada`)
  } catch (error) {
    console.error("❌ Error limpiando registros temporales:", error)
    throw error
  }
}

// Helper function to validate dynamic user data
export const validateDynamicUser = (user: DynamicUser, schema: DynamicSchema): { valid: boolean; errors: string[] } => {
  const errors: string[] = []

  // Check if user has ID
  if (!user.id) {
    errors.push("Usuario debe tener un ID válido")
  }

  // Check required fields based on schema
  Object.entries(schema).forEach(([fieldName, fieldConfig]) => {
    if (fieldConfig.required) {
      const value = user[fieldName] !== undefined ? user[fieldName] : user.extra?.[fieldName]
      if (value === null || value === undefined || value === '') {
        errors.push(`Campo requerido: ${fieldConfig.label}`)
      }
    }
  })

  return {
    valid: errors.length === 0,
    errors
  }
}
